<!-- app-subdomain-creators\src\accountPages\CreatorMemberList.vue -->

<template>
  <CreatorPermDeleteMember
    :is-open="showDeleteModal"
    :member-id="showDeleteModal ? selectedMemberId : null"
    class="z-50"
    @confirm-delete="confirmDeleteMember"
    @close="closeDeleteModal"
  />
  <CreatorManualMemberLeadModal
    v-model:open="showCreateMemberModal"
    mode="member"
    @saved="handleManualMemberSaved"
  />
  <CreatorBulkImportModal
    v-model:open="showBulkImportMembersModal"
    target-type="member"
    @import-complete="handleBulkImportMembersComplete"
  />
  <CreatorPlanLimitPrompt :open="showLimitPrompt" :resource="memberUsage" @close="showLimitPrompt = false" />
  
  <div class="px-4 sm:px-6 lg:px-24">
    <!-- Tool Section -->
    <div class="flex flex-wrap items-center justify-between gap-3 pt-2">
      <!-- Search Bar -->
      <div class="relative mx-auto max-w-[500px] flex-1">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search Members..."
          class="w-full rounded-md border border-gray-300 px-4 py-2 text-sm focus:border-[#2D8CFF] focus:ring-[#2D8CFF] focus:outline-none pr-10"
        >
        <MagnifyingGlassIcon
          class="absolute inset-y-2 right-3 h-5 w-5 text-gray-400 pointer-events-none"
        />
      </div>
      <div class="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          class="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-pink-500 transition hover:bg-pink-50 hover:text-pink-600"
          @click="openMemberCreation"
        >
          <UserPlusIcon class="h-6 w-6" />
          <span
            class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0"
          >
            Create Member
          </span>
        </button>
        <button
          type="button"
          class="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-purple-500 transition hover:bg-purple-50 hover:text-purple-600"
          @click="openMemberImport"
        >
          <ArrowUpTrayIcon class="h-6 w-6" />
          <span
            class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0"
          >
            Upload Members
          </span>
        </button>
        <button
          type="button"
          class="group relative inline-flex h-11 w-11 items-center justify-center rounded-full text-amber-500 transition hover:bg-amber-50 hover:text-amber-600 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="isExportingMembers"
          @click="exportMembersCsv"
        >
          <ArrowPathIcon
            v-if="isExportingMembers"
            class="h-6 w-6 animate-spin"
          />
          <ArrowDownTrayIcon
            v-else
            class="h-6 w-6"
          />
          <span
            class="pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 rounded-xl bg-black/70 px-2 py-1.5 text-[11px] text-white whitespace-nowrap shadow-lg opacity-0 translate-y-1 transition-all duration-150 group-hover:opacity-100 group-hover:translate-y-0"
          >
            Export Members
          </span>
        </button>
      </div>
    </div>
    <p
      v-if="controlNoticeMessage"
      class="mt-2 text-sm text-zinc-500"
    >
      {{ controlNoticeMessage }}
    </p>
    <div v-if="memberUsage && ['warning', 'at_limit', 'blocked'].includes(memberUsage.state)" class="mt-4 flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <p class="font-semibold text-amber-950">You are {{ memberUsage.remaining === 0 ? 'at' : 'approaching' }} your member limit</p>
        <p class="mt-1 text-sm text-amber-800">{{ memberUsage.usage }} of {{ memberUsage.publicLimit }} member spaces are in use<span v-if="memberUsage.remaining !== null"> · {{ memberUsage.remaining }} remaining</span>.</p>
      </div>
      <button type="button" class="shrink-0 rounded-xl bg-[#2D8CFF] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1979e9]" @click="showLimitPrompt = true">Upgrade plan</button>
    </div>
    
    <div class="flex justify-between items-center mt-2 mx-2">
      <!-- Selected Count -->
      <div
        v-if="totalSelected > 0"
        class="flex items-center text-sm text-gray-700"
      >
        Selected
        <span class="font-medium mx-1">{{ totalSelected }}</span>
        of
        <span class="font-medium mx-1">{{ totalMembers }}</span>
  
        <!-- Actions Menu -->
        <div class="relative ml-4 actions-menu">
          <button
            class="flex items-center text-sm text-[#2D8CFF] font-semibold hover:underline"
            @click="toggleActionsMenu"
          >
            Actions
            <ChevronDownIcon
              :class="actionsMenuOpen ? 'rotate-180' : ''"
              class="ml-1 w-4 h-4 transform transition-transform"
            />
          </button>
          <div
            v-if="actionsMenuOpen"
            class="absolute z-10 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg"
          >
            <ul>
              <li>
                <button
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  @click="handleAction('delete')"
                >
                  Delete
                </button>
              </li>
              <li>
                <button
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  @click="handleAction('export')"
                >
                  Export
                </button>
              </li>
              <li>
                <button
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  @click="handleAction('productExpose')"
                >
                  Product Expose
                </button>
              </li>
              <li>
                <button
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  @click="handleAction('productHide')"
                >
                  Product Hide
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
     
    
      <!-- Members Count (Right-Aligned) -->
      <div class="flex items-end gap-2 text-right text-sm text-gray-700 ml-auto">
        <span class="mb-[1px]">All Members</span>
        <span class="text-2xl font-bold text-[#2D8CFF]">{{ totalMembers }}</span>
      </div>
    </div>
    
    <div class="mt-2 flow-root">
      <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:mx-2">
        <div class="inline-block min-w-full py-2 align-middle">
          <table
            v-if="filteredMembers.length > 0"
            class="min-w-full divide-y divide-gray-300"
          >
            <thead>
              <tr>
                <th
                  scope="col"
                  class="pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  <div class="flex h-6 items-center">
                    <input
                      id="select-all"
                      type="checkbox"
                      :checked="selectAll"
                      class="h-5 w-5 rounded border-gray-300 text-[#2D8CFF] focus:ring-[#2D8CFF]"
                      @change="toggleSelectAll"
                    >
                  </div>
                </th>
                <th
                  scope="col"
                  class="py-3.5 pl-6 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                >
                  Profile
                </th>
                    
                <th
                  scope="col"
                  class="px-6 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Subscribed On
                </th>
                <th
                  scope="col"
                  class="px-6 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Status
                </th>
                <th
                  scope="col"
                  class="px-6 py-3.5 text-left text-sm font-semibold text-gray-900"
                >
                  Subscription Plan
                </th>
                <th
                  scope="col"
                  class="relative py-3.5 pl-6 pr-4 sm:pr-6"
                >
                  <span class="sr-only">Edit</span>
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 bg-white">
              <tr
                v-for="(member, index) in paginatedMembers"
                :key="member.email"
                :class="{
                  'relative': deletingRowId === member.userId,
                  'cursor-pointer hover:bg-zinc-100': deletingRowId !== member.userId
                }"
                @click="deletingRowId !== member.userId && goToMemberDetails(member.userId)"
              >
                <td class="pl-4 pr-3 text-left text-sm sm:pl-6">
                  <div class="flex h-6 items-center">
                    <input
                      :id="'select-' + index"
                      v-model="selected[index + (currentPage - 1) * itemsPerPage]"
                      type="checkbox"
                      class="h-5 w-5 rounded border-gray-300 text-[#2D8CFF] focus:ring-[#2D8CFF]"
                      @click.stop="updateSelectAllState"
                    >
                  </div>
                </td>


                <td class="whitespace-nowrap py-5 pl-6 pr-3 text-sm">
                  <div class="flex items-center">
                    <div class="h-11 w-11 flex-shrink-0">
                      <div class="relative">
                        <img
                          class="h-11 w-11 rounded-full object-cover"
                          :src="member.image"
                          alt=""
                        >
                        <div
                          class="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full border-2 border-white"
                          :class="{
                            'bg-green-500': memberStatus(member) === 'online',
                            'bg-yellow-400': memberStatus(member) === 'away',
                            'bg-gray-300': memberStatus(member) !== 'online' && memberStatus(member) !== 'away'
                          }"
                        />
                      </div>
                    </div>
                    <div class="ml-4">
                      <div class="font-medium text-gray-900">
                        {{ member.name }}
                      </div>
                      <div class="mt-1 text-gray-500">
                        {{ member.email }}
                      </div>
                    </div>
                  </div>
                </td>



                <td class="whitespace-nowrap px-6 py-5 text-sm text-gray-500">
                  <div class="text-gray-900">
                    {{ member.subscribeDate }}
                  </div>
                </td>
                <td class="whitespace-nowrap px-6 py-5 text-sm text-gray-500">
                  <span
                    :class="badgeClass(member.subscriptionStatus)"
                    class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                  >
                    {{ member.subscriptionStatus }}
                  </span>
                </td>
                <td class="whitespace-nowrap px-6 py-5 text-sm text-gray-500">
                  <span
                    :class="badgeClassPlans(member.subscriptionPlan)"
                    class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                  >
                    {{ displaySubscriptionPlan(member.subscriptionPlan) }}
                  </span>
                </td>
   
                <!-- Individual User Menu in the table -->
                <td class="relative whitespace-nowrap py-5 pl-6 pr-4 text-right text-sm font-medium sm:pr-6">
                  <div class="relative user-dropdown w-11 h-11 p-2 hover:bg-zinc-200 rounded-full items-center">
                    <EllipsisVerticalIcon
                      class="text-zinc-500 h-7 cursor-pointer"
                      @click.stop="toggleUserMenu(index)"
                    />
                    <div
                      v-if="openMenuIndex === index"
                      class="absolute z-10 -mt-2 right-12 w-40 bg-white border border-zinc-200 rounded-md shadow-lg"
                    >
                      <ul>
                        <li>
                          <button
                            class="block w-full text-left px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 font-normal"
                            @click="goToMemberDetails(member.userId)"
                          >
                            Show Details
                          </button>
                        </li>
                        <li>
                          <!-- Delete button within dropdown -->
                          <button
                            class="block w-full text-left px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 font-normal"
                            @click.stop="handleUserAction('delete', index)"
                          >
                            Delete Member
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </td>
  
                <!-- Deletion Overlay -->
                <transition
                  name="fade-scale"
                  mode="out-in"
                  enter-active-class="transition duration-500 ease-out"
                  leave-active-class="transition duration-500 ease-in"
                  enter-from-class="opacity-0 scale-95"
                  enter-to-class="opacity-100 scale-100"
                  leave-from-class="opacity-100 scale-100"
                  leave-to-class="opacity-0 scale-95"
                >
                  <div
                    v-if="deletingRowId === member.userId"
                    class="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center rounded-md backdrop-blur-sm"
                  >
                    <div class="mx-auto">
                      <div class="flex items-center gap-2 animate-fade-in">
                        <transition
                          name="icon-fade-scale"
                          mode="out-in"
                          enter-active-class="transition transform delay-150 duration-700 ease-out"
                          leave-active-class="transition duration-500 ease-in"
                          enter-from-class="opacity-0 scale-50"
                          enter-to-class="opacity-100 scale-100"
                        >
                          <CheckCircleIcon
                            v-if="deletingRowId === member.userId"
                            class="h-10 text-green-500 transition transform delay-150 duration-700 ease-out scale-125"
                          />
                        </transition>
                        <p class="text-lg text-green-500">
                          Successfully Deleting...
                        </p>
                      </div>
                    </div>
                  </div>
                </transition>
              </tr>
            </tbody>
          </table>
          <section
            v-else-if="membersLoaded && !memberLoadError && state.members.length === 0 && !searchQuery.trim()"
            class="overflow-hidden rounded-[28px] border border-zinc-200 bg-white shadow-sm"
          >
            <div class="grid min-h-[490px] lg:grid-cols-[1.05fr_0.95fr]">
              <div class="flex flex-col justify-center px-7 py-10 sm:px-10 lg:px-12">
                <span class="mb-5 inline-flex w-fit items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-blue-700">
                  <UserGroupIcon class="h-4 w-4" />
                  Members · Subscribers
                </span>
                <h2 class="max-w-xl text-3xl font-bold tracking-tight text-zinc-950 sm:text-4xl">
                  Bring your subscribers together in one place.
                </h2>
                <p class="mt-5 max-w-xl text-base leading-7 text-zinc-600">
                  Create a member manually or upload your existing list. From here, you can manage subscriber profiles, membership details, access, and the custom data that makes every relationship yours.
                </p>
                <div class="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button type="button" class="inline-flex items-center justify-center gap-2 rounded-xl bg-[#2478ff] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-600" @click="openMemberCreation">
                    <UserPlusIcon class="h-4 w-4" />
                    Create a member
                  </button>
                  <button type="button" class="inline-flex items-center justify-center gap-2 rounded-xl border border-zinc-300 bg-white px-5 py-3 text-sm font-semibold text-zinc-800 transition hover:bg-zinc-50" @click="openMemberImport">
                    <ArrowUpTrayIcon class="h-4 w-4" />
                    Upload members
                  </button>
                </div>
                <div class="mt-9 grid gap-4 border-t border-zinc-100 pt-7 sm:grid-cols-3">
                  <div v-for="item in memberEmptySteps" :key="item.label" class="flex gap-3 sm:block">
                    <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-950 text-xs font-bold text-white">{{ item.step }}</span>
                    <div class="sm:mt-3">
                      <p class="text-sm font-semibold text-zinc-900">{{ item.label }}</p>
                      <p class="mt-1 text-xs leading-5 text-zinc-500">{{ item.copy }}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div class="relative flex min-h-[350px] items-center justify-center overflow-hidden bg-gradient-to-br from-[#ecf4ff] via-white to-cyan-100 p-6 sm:p-10">
                <div class="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-blue-300/30 blur-3xl" />
                <div class="absolute -bottom-20 -left-16 h-64 w-64 rounded-full bg-cyan-300/30 blur-3xl" />
                <div class="relative w-full max-w-[390px] rounded-[26px] border border-white/80 bg-white/85 p-5 shadow-2xl shadow-blue-900/10 backdrop-blur">
                  <div class="flex items-center justify-between border-b border-zinc-100 pb-4">
                    <div>
                      <p class="text-[11px] font-bold uppercase tracking-[0.15em] text-blue-600">Subscriber workspace</p>
                      <p class="mt-1 text-lg font-semibold text-zinc-900">All your members</p>
                    </div>
                    <span class="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">Organized</span>
                  </div>
                  <div class="mt-4 space-y-3">
                    <div v-for="item in memberPreviewRows" :key="item.label" class="flex items-center gap-3 rounded-xl border border-zinc-100 bg-white p-3">
                      <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full" :class="item.avatarClass"><UserIcon class="h-5 w-5" /></span>
                      <div class="min-w-0 flex-1">
                        <p class="text-sm font-semibold text-zinc-800">{{ item.label }}</p>
                        <p class="text-xs text-zinc-400">{{ item.detail }}</p>
                      </div>
                      <span class="h-2 w-2 rounded-full bg-emerald-400" />
                    </div>
                  </div>
                  <div class="mt-4 flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-3 text-xs font-medium text-blue-800">
                    <AdjustmentsHorizontalIcon class="h-4 w-4 shrink-0" />
                    Profiles, plans, access, and custom details
                  </div>
                </div>
              </div>
            </div>
          </section>
          <div v-else-if="memberLoadError" class="rounded-2xl border border-amber-200 bg-amber-50 px-6 py-8 text-center text-sm text-amber-900">
            <p>{{ memberLoadError }}</p>
            <button type="button" class="mt-3 font-semibold text-blue-700 hover:underline" @click="fetchMembers">Try again</button>
          </div>
          <div
            v-else-if="!membersLoaded"
            class="min-w-[840px] overflow-hidden rounded-2xl border border-zinc-200 bg-white"
            role="status"
            aria-label="Loading members"
            aria-busy="true"
          >
            <div
              class="grid grid-cols-[40px_minmax(220px,1.7fr)_minmax(120px,1fr)_minmax(100px,0.75fr)_minmax(140px,1fr)_44px] items-center gap-6 border-b border-zinc-200 px-6 py-4"
              aria-hidden="true"
            >
              <span class="h-5 w-5 rounded bg-zinc-200" />
              <span class="h-3 w-16 rounded-full bg-zinc-200" />
              <span class="h-3 w-24 rounded-full bg-zinc-200" />
              <span class="h-3 w-14 rounded-full bg-zinc-200" />
              <span class="h-3 w-28 rounded-full bg-zinc-200" />
              <span />
            </div>
            <div
              v-for="row in 6"
              :key="`member-skeleton-${row}`"
              class="grid animate-pulse grid-cols-[40px_minmax(220px,1.7fr)_minmax(120px,1fr)_minmax(100px,0.75fr)_minmax(140px,1fr)_44px] items-center gap-6 border-b border-zinc-100 px-6 py-4 last:border-b-0"
              aria-hidden="true"
            >
              <span class="h-5 w-5 rounded bg-zinc-200" />
              <span class="flex items-center gap-4">
                <span class="h-11 w-11 shrink-0 rounded-full bg-zinc-200" />
                <span class="flex flex-1 flex-col gap-2">
                  <span class="h-3.5 w-28 rounded-full bg-zinc-200" />
                  <span class="h-3 w-40 rounded-full bg-zinc-100" />
                </span>
              </span>
              <span class="h-3.5 w-24 rounded-full bg-zinc-200" />
              <span class="h-6 w-20 rounded-md bg-zinc-100" />
              <span class="h-6 w-28 rounded-md bg-zinc-100" />
              <span class="h-9 w-9 rounded-full bg-zinc-100" />
            </div>
            <span class="sr-only">Loading members</span>
          </div>
          <!-- Empty Search Message -->
          <div
            v-else
            class="text-center py-10 text-gray-500 text-sm"
          >
            We can't find any results from this search. Please try again.
          </div>
        </div>
      </div>
    </div>
    
    <!-- Pagination Controls -->
    <div
      v-if="totalMembers > 0"
      class="flex justify-between items-center mx-2 py-5 mb-6 border-t"
    >
      <div class="text-sm text-gray-700">
        Showing
        <span class="font-medium">{{ startIndex }}</span>
        to
        <span class="font-medium">{{ endIndex }}</span>
        of
        <span class="font-medium">{{ totalMembers }}</span>
        results
      </div>
      <div class="flex gap-4 items-center">
        <label class="flex items-center gap-2 text-sm text-gray-700">
          <span>Rows per page:</span>
          <PlatformaSelect
            v-model.number="itemsPerPage"
            class="min-w-[110px]"
            :options="rowsPerPageOptions"
            menu-placement="top"
          />
        </label>
        <div class="text-sm text-gray-700">
          Page 
          <span class="font-medium">{{ currentPage }}</span> 
          of 
          <span class="font-medium">{{ totalPages }}</span> 
        </div>
        <div class="gap-2 flex items-center">
          <button
            :disabled="currentPage === 1"
            class="relative inline-flex items-center px-[10px] py-[6px] border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-[#2D8CFF] active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            @click="prevPage"
          >
            Previous
          </button>
          <button
            :disabled="currentPage >= totalPages"
            class="relative inline-flex items-center px-[10px] py-[6px] border border-gray-300 text-xs font-medium rounded-lg text-gray-700 bg-white hover:text-gray-500 focus:outline-none focus:ring-blue focus:border-[#2D8CFF] active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150"
            @click="nextPage"
          >
            Next
          </button>
        </div>
      </div>
    </div>
    
  </div>
</template>
    
    
    <script setup>
    import { ref, onMounted, onBeforeUnmount, computed, reactive, watch } from 'vue';
    import { MagnifyingGlassIcon, ChevronDownIcon, EllipsisVerticalIcon, CheckCircleIcon, UserPlusIcon, UserGroupIcon, UserIcon, AdjustmentsHorizontalIcon, ArrowUpTrayIcon, ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/vue/24/outline';
    import { useRouter } from 'vue-router';
    // import { onNewMember } from '@/services/websocketService';
    import CreatorPermDeleteMember from '@/Components/CreatorPermDeleteMember.vue';
    import CreatorManualMemberLeadModal from '@/Components/CreatorManualMemberLeadModal.vue';
    import CreatorBulkImportModal from '@/Components/CreatorBulkImportModal.vue';
    import CreatorPlanLimitPrompt from '@/Components/CreatorPlanLimitPrompt.vue';
    import PlatformaSelect from '@/Components/PlatformaSelect.vue';

    import { useWebsocketStore } from '@/stores/websocketStore';
    import { usePresenceStore } from '@/stores/presenceStore';

    const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
    
    // Access the router instance
    const router = useRouter();

    // 🔥 presence + WS stores
    const websocketStore = useWebsocketStore();
    const presenceStore = usePresenceStore();
    
    const membersLoaded = ref(false);
    const memberLoadError = ref('');
    const memberEmptySteps = [
      { step: 1, label: 'Add subscribers', copy: 'Create one profile or import your list.' },
      { step: 2, label: 'Shape access', copy: 'Keep plans and member details together.' },
      { step: 3, label: 'Stay connected', copy: 'Manage everyone as your audience grows.' },
    ];
    const memberPreviewRows = [
      { label: 'Member profiles', detail: 'Your audience at a glance', avatarClass: 'bg-blue-100 text-blue-700' },
      { label: 'Membership plans', detail: 'Access and subscriptions', avatarClass: 'bg-violet-100 text-violet-700' },
      { label: 'Custom details', detail: 'The data that matters to you', avatarClass: 'bg-cyan-100 text-cyan-700' },
    ];
    
    // Local state for the members list, synced with the centralized state
    // Declare members as a reactive object
  const state = reactive({
    members: []
  });
  const showCreateMemberModal = ref(false);
  const showBulkImportMembersModal = ref(false);
  const controlNoticeMessage = ref('');
  const isExportingMembers = ref(false);
  const memberUsage = ref(null);
  const showLimitPrompt = ref(false);

  const loadMemberUsage = async () => {
    try {
      const response = await fetch(`${apiProxyUrl}/api/platforma-creator-billing/usage-limits`, { credentials: 'include', cache: 'no-store' });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data?.success) memberUsage.value = data?.resources?.members || null;
    } catch (_error) {
      memberUsage.value = null;
    }
  };

  const memberLimitReached = () => memberUsage.value
    && ['at_limit', 'blocked'].includes(String(memberUsage.value.state || ''));
  const openMemberCreation = () => {
    if (memberLimitReached()) { showLimitPrompt.value = true; return; }
    showCreateMemberModal.value = true;
  };
  const openMemberImport = () => {
    if (memberLimitReached()) { showLimitPrompt.value = true; return; }
    showBulkImportMembersModal.value = true;
  };

  // 🔥 presence helpers
  const getMemberPresenceId = (member) => {
    if (!member) return null;
    return member.userId ?? member.user_id ?? null;
  };

  const memberStatus = (member) => {
    const id = getMemberPresenceId(member);
    if (!id) return 'offline';
    return presenceStore.memberStatus(id); // 'online' | 'away' | 'offline'
  };

    
  // function handleNewMember(newMember) {
  //     console.log("Received new member data:", newMember);
  
  //     // Format the new member data with a fallback image
  //     const formattedMember = {
  //         userId: newMember.user_id,
  //         name: `${newMember.firstName} ${newMember.lastName}`,
  //         subscribeDate: new Date(newMember.created_at).toLocaleDateString(),
  //         subscriptionDate: new Date(newMember.created_at),
  //         subscriptionStatus: newMember.status,
  //         subscriptionPlan: newMember.subscriptionPlanName,
  //         email: newMember.email,
  //         // If profilePic is empty or not accessible, use the default image
  //         image: newMember.profilePic && newMember.profilePic !== 'src/assets/media/images/default-avatar.png'
  //             ? newMember.profilePic
  //             : require('@/assets/media/images/default-avatar.png')
  //     };
  
  //     // Add the new member to the start of the array
  //     state.members.unshift(formattedMember);
  //     console.log("Added new member to the start of members array:", state.members);
  
  //     // Sort members array by the most recent subscription date
  //     state.members.sort((a, b) => b.subscriptionDate - a.subscriptionDate);
  //     console.log("Sorted members array by most recent:", state.members);
  // }
  
    

   // Fetch members initially using the API
    const fetchMembers = async () => {
  try {
    const response = await fetch(`${apiProxyUrl}/api/members`, {
      method: 'GET',
      credentials: 'include',
    });

    // Attempt to parse the response JSON and log it
    const data = await response.json();

    // This endpoint reports an empty member list as a 404 instead of 200 [].
    if (response.status === 404 && data?.message === 'No members found.') {
      state.members = [];
      memberLoadError.value = '';
      membersLoaded.value = true;
      return;
    }

    // Handle the data if successful
        if (response.ok && data.success) {
      memberLoadError.value = '';
      membersLoaded.value = true;

      // Map and sort members
      state.members = data.members
        .map((member) => ({
          userId: member.user_id,
          name: `${member.first_name} ${member.last_name}`,
          subscribeDate: new Date(member.created_at).toLocaleDateString(),
          subscriptionDate: new Date(member.created_at),
          subscriptionStatus: String(member.subscription_status || member.status || '').trim() || 'Active',
          subscriptionPlan: member.subscription_plan_name,
          email: member.email,
          historySearchText: String(member.profile_history_search_text || '').trim(),
          image: member.profile_pic && member.profile_pic !== 'src/assets/media/images/default-avatar.png'
            ? member.profile_pic
            : require('@/assets/media/images/default-avatar.png'),
        }))
        .sort((a, b) => b.subscriptionDate - a.subscriptionDate);

      // 🔥 subscribe to presence for all members in this list
      const memberIdsForPresence = state.members
        .map((m) => getMemberPresenceId(m))
        .filter(Boolean);

      if (memberIdsForPresence.length) {
        websocketStore.send({
          type: 'creator',
          event: 'presence:subscribeMembers',
          payload: {
            memberIds: memberIdsForPresence,
          },
        });
      }

    } else {
      memberLoadError.value = 'We could not load your members right now.';
      console.error('Error in response from proxy:', data.message);
    }
  } catch (error) {
    // Log any errors encountered in the fetch process
    memberLoadError.value = 'We could not load your members right now.';
    console.error('Error fetching members:', error);
  }
};

let lifecycleRefreshTimer = null;

const onMemberLifecycle = (event) => {
  const payload = event?.detail || {};
  const eventType = String(payload?.event || '').trim().toLowerCase();
  if (!eventType) return;

  if (lifecycleRefreshTimer) {
    clearTimeout(lifecycleRefreshTimer);
    lifecycleRefreshTimer = null;
  }
  lifecycleRefreshTimer = setTimeout(() => {
    lifecycleRefreshTimer = null;
    void fetchMembers();
  }, eventType === 'member_joined' ? 120 : 180);
};

const handleManualMemberSaved = () => {
  showCreateMemberModal.value = false;
  controlNoticeMessage.value = 'Member saved.';
  setTimeout(() => {
    controlNoticeMessage.value = '';
  }, 3500);
  void fetchMembers();
  void loadMemberUsage();
};

const handleBulkImportMembersComplete = (job = {}) => {
  const successRows = Number(job?.successRows || 0);
  const failedRows = Number(job?.failedRows || 0);
  const skippedRows = Number(job?.skippedRows || 0);
  controlNoticeMessage.value = `Import finished: ${successRows} success, ${failedRows} failed, ${skippedRows} skipped.`;
  setTimeout(() => {
    if (controlNoticeMessage.value.startsWith('Import finished:')) controlNoticeMessage.value = '';
  }, 6000);
  void fetchMembers();
};

const exportMembersCsv = async () => {
  if (isExportingMembers.value) return;
  isExportingMembers.value = true;
  try {
    await new Promise((resolve) => setTimeout(resolve, 0));
    const rows = Array.isArray(filteredMembers.value) ? filteredMembers.value : [];
    const header = ['name', 'email', 'subscribed_on', 'status', 'subscription_plan'];
    const csvRows = rows.map((member) => [
      `"${String(member?.name || '').replace(/"/g, '""')}"`,
      `"${String(member?.email || '').replace(/"/g, '""')}"`,
      `"${String(member?.subscribeDate || '').replace(/"/g, '""')}"`,
      `"${String(member?.subscriptionStatus || '').replace(/"/g, '""')}"`,
      `"${String(member?.subscriptionPlan || '').replace(/"/g, '""')}"`,
    ].join(','));
    const csvText = [header.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `platforma-members-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } finally {
    isExportingMembers.value = false;
  }
};

    
    // Connect WebSocket when the component is mounted
    onMounted(() => {
      websocketStore.connect();   // 🔥 ensure WS is up for presence
      fetchMembers();
      loadMemberUsage();
      if (typeof window !== 'undefined') {
        window.addEventListener('creator:member-lifecycle', onMemberLifecycle);
      }
      // onNewMember(handleNewMember);
    });
    
    // Class logic for subscription statuses
    const badgeClass = (status) => {
      switch (String(status || '').trim()) {
        case 'Cancelled':
          return 'bg-red-50 text-red-700 ring-red-600/20';
        case 'Active':
          return 'bg-blue-50 text-blue-700 ring-blue-600/20';
        case 'Trialing':
          return 'bg-sky-50 text-sky-700 ring-sky-600/20';
        case 'Paused':
          return 'bg-zinc-100 text-zinc-700 ring-zinc-400/20';
        case 'Subscribed':
          return 'bg-green-50 text-green-700 ring-green-600/20';
        case 'Cancelling':
          return 'bg-red-50 text-red-700 ring-red-600/20';
        case 'Waiting for Payment':
          return 'bg-amber-50 text-amber-700 ring-amber-600/20';
        case 'Failed Payment':
          return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20';
        case 'No Plan':
          return 'bg-zinc-100 text-zinc-600 ring-zinc-500/20';
        case 'Custom Subscription':
          return 'bg-purple-50 text-purple-700 ring-purple-600/20';
        default:
          return 'bg-green-50 text-green-700 ring-green-600/20';
      }
    };
    
    // Class logic for subscription plans
    const isCustomPlanLabel = (value) => String(value || '').trim().toLowerCase().includes('custom');

    const displaySubscriptionPlan = (value) => {
      if (isCustomPlanLabel(value)) return 'Custom';
      return String(value || '').trim() || 'Basic';
    };

    const badgeClassPlans = (status) => {
      if (isCustomPlanLabel(status)) {
        return 'bg-purple-50 text-purple-700 ring-purple-600/20';
      }
      switch (String(status || '').trim()) {
        case 'Free Pass':
          return 'bg-blue-50 text-blue-700 ring-blue-600/20';
        default:
          return 'bg-green-50 text-green-700 ring-green-600/20';
      }
    };
    
    // Search state
    const searchQuery = ref('');
    
    // Filter and paginate members based on search and pagination state
    // Computed property now references state.members
  const filteredMembers = computed(() => {
      if (searchQuery.value) {
          return state.members.filter((member) => {
              return (
                   (member.name?.toLowerCase() || '').includes(searchQuery.value.toLowerCase()) ||
                   (member.email?.toLowerCase() || '').includes(searchQuery.value.toLowerCase()) ||
                   (member.subscribeDate?.toLowerCase() || '').includes(searchQuery.value.toLowerCase()) ||
                   (member.historySearchText?.toLowerCase() || '').includes(searchQuery.value.toLowerCase())
              );
          });
      }
      return state.members;
  });
    
    const currentPage = ref(1);
    const itemsPerPage = ref(6);
const rowsPerPageOptions = Object.freeze([
  { value: 6, label: '6' },
  { value: 12, label: '12' },
  { value: 24, label: '24' },
  { value: 50, label: '50' },
  { value: 100, label: '100' },
  { value: 500, label: '500' },
]);
    const totalMembers = computed(() => filteredMembers.value.length);
    const totalPages = computed(() => Math.max(1, Math.ceil(totalMembers.value / Math.max(1, Number(itemsPerPage.value || 1)))));
    
    const paginatedMembers = computed(() => {
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const end = start + itemsPerPage.value;
      return filteredMembers.value.slice(start, end);
    });
    
    const startIndex = computed(() => {
      if (!totalMembers.value) return 0;
      return (currentPage.value - 1) * itemsPerPage.value + 1;
    });
    const endIndex = computed(() => Math.min(currentPage.value * itemsPerPage.value, totalMembers.value));
    
    const nextPage = () => {
      if (currentPage.value < totalPages.value) {
        currentPage.value++;
      }
    };
    
    const prevPage = () => {
      if (currentPage.value > 1) {
        currentPage.value--;
      }
    };

    watch([searchQuery, itemsPerPage], () => {
      currentPage.value = 1;
    });

    watch(totalPages, (nextTotal) => {
      if (currentPage.value > nextTotal) {
        currentPage.value = nextTotal;
      }
    });
    
    // Selection logic
    const selected = ref(state.members.map(() => false));
  
    
    const selectAll = computed({
      get() {
        return selected.value.every((isSelected) => isSelected);
      },
      set(value) {
        selected.value = selected.value.map(() => value);
      },
    });
    
    const toggleSelectAll = () => {
      const newValue = !selectAll.value;
      selected.value = selected.value.map(() => newValue);
    };
    
    const updateSelectAllState = () => {
      if (selectAll.value && !selected.value.every((isSelected) => isSelected)) {
        selectAll.value = false;
      } else if (!selectAll.value && selected.value.every((isSelected) => isSelected)) {
        selectAll.value = true;
      }
    };
    
    // Computed property for total selected count
    const totalSelected = computed(() => selected.value.filter((isSelected) => isSelected).length);
    
    // Dropdown menu state
    const actionsMenuOpen = ref(false);
    
    const toggleActionsMenu = () => {
      actionsMenuOpen.value = !actionsMenuOpen.value;
    };
    
    const handleAction = () => {
      actionsMenuOpen.value = false;
  
    };
    
    // Unified method to close both menus when clicking outside
    const handleClickOutside = (event) => {
      const actionsDropdown = document.querySelector('.actions-menu');
      const userDropdowns = document.querySelectorAll('.user-dropdown');
    
      let clickedOutside = true;
    
      if (actionsDropdown && actionsDropdown.contains(event.target)) {
        clickedOutside = false;
      }
    
      userDropdowns.forEach((dropdown) => {
        if (dropdown.contains(event.target)) {
          clickedOutside = false;
        }
      });
    
      if (clickedOutside) {
        actionsMenuOpen.value = false;
        openMenuIndex.value = null;
      }
    };
    
    onMounted(() => {
      document.addEventListener('click', handleClickOutside);
    });
    
    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside);
      if (typeof window !== 'undefined') {
        window.removeEventListener('creator:member-lifecycle', onMemberLifecycle);
      }
      if (lifecycleRefreshTimer) {
        clearTimeout(lifecycleRefreshTimer);
        lifecycleRefreshTimer = null;
      }
    });
    
    // State management for open menus
    const openMenuIndex = ref(null);
    
    const toggleUserMenu = (index) => {
      openMenuIndex.value = openMenuIndex.value === index ? null : index;
    };
    
    const handleUserAction = (action, index) => {
    openMenuIndex.value = null;
  
    const member = paginatedMembers.value[index];
    if (action === 'delete' && member) {
      selectedMemberId.value = member.userId;  // Set the selected member ID
      showDeleteModal.value = true;              // Show the modal
    } 
  };
    
  // Method to navigate to member details
  // Emits the selected member when navigating
  const goToMemberDetails = (userId) => {
    const member = state.members.find((c) => c.userId === userId);
    if (member) {
      window.dispatchEvent(new CustomEvent('member-selected', { detail: member }));
      router.push({ name: 'MemberDetails', params: { memberId: userId } });
    }
  };
  
  const deleteMember = async () => {
  try {
    const userId = selectedMemberId.value;
    const response = await fetch(`${apiProxyUrl}/api/members/${userId}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    const data = await response.json();

    if (response.ok && data.success) {
      state.members = state.members.filter(member => member.userId !== userId);
    } else {
      alert('Error: ' + data.message);
    }
  } catch (error) {
  
    alert('An error occurred while deleting the member.');
  } finally {
    showDeleteModal.value = false;
    selectedMemberId.value = null;
    deletingRowId.value = null;
  }
};
  
  const showDeleteModal = ref(false);
  const selectedMemberId = ref(null);
  const closeDeleteModal = () => {
    showDeleteModal.value = false;
    selectedMemberId.value = null;
  };
  
  const deletingRowId = ref(null); // Track which row is being deleted
  
  // Modified delete function with overlay effect
  const confirmDeleteMember = (memberId) => {
    deletingRowId.value = memberId;
    setTimeout(() => {
      deleteMember(memberId);
    }, 3500); // 3.5 seconds delay for feedback effect
  };
  </script> 
